<template>
  <div class="map-container">
    <div class="map-content position-relative">
      <div id='map' ref="mapElement" class="map-custom-class m-0 p-0"></div>
      <OnThisMapButtons :button-clicked="buttonClicked" :buttons="buttons" :maps-button-id="0"/>
      <TrailDataCard/>
      <WaypointAddingNotification />
      <MapControls :show-fullscreen="true"/>
      <Loader v-if="miniLoader"/>
      <MapContextMenu />
      <div class="a-little-class"></div>
      <div id="weather-legend-container"></div>
      <LandAccessLegend :visible="landAccessActive" />
      <MapCrosshair
        v-if="infoBarSettings.crosshairEnabled"
        :variant="infoBarSettings.crosshairStyle"
        :color="crosshairColorHex"
      />
      <div v-if="interactionsLocked" class="map-lock-badge" role="status">
        <span class="material-icons">lock</span> {{ $t('map.map_locked') }}
      </div>
      <div class="bottom-controls-container">
        <MapInfoBar
          :settings="infoBarSettings"
          :bearing="currentBearing"
          @open-settings="openInfoBarSettings"
          @reset-north="resetNorth"
        />
        <ScaleBar v-if="mapInstance" :map="mapInstance" :latitude="currentLatitude" />
      </div>

      <HikeMeasurementPanel v-if="isActive" context="main" />
      <Teleport to="body">
        <LoadingDialog :show-loading-dialog="loading"/>
        <MapInfoBarSettingsDialog
          :interactions-locked="interactionsLocked"
          @update:interactionsLocked="setInteractionsLocked"
        />
        <GoToLocationDialog/>
        <WaypointDialog/>
        <BaseMapStylesDialog/>
        <MapDataPOIDetailDialog/>
        <MapOptionsDialog
          :edit-hike="false"
          :show-create="true"
          @create="openPlanner"
          @waypoints="openWaypoints"
          @goto="showGoTo"
          @print="openPrintMap"
          @measurement="openMeasurement"
          :show-measurement="true"
        />
      </Teleport>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import {mapState} from "vuex"; // mapState still works with Vuex 4

// --- Library Imports ---
import mapboxgl from 'mapbox-gl';
import {throttle} from 'lodash-es'; // Ensure lodash-es is installed

// --- Component Imports ---
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import BaseMapStylesDialog from ".././base_map_dialog/BaseMapStylesDialog.vue";
import GoToLocationDialog from "../go_to_location_dialog/GoToLocationDialog.vue";
import MapControls from "../components/MapControls.vue";
import WaypointDialog from "../waypoint_dialog/WaypointDialog.vue";
import WaypointAddingNotification from "../waypoint_adding_mode_notificaiton/WaypointAddingNotification.vue";
import TrailDataCard from "../trail_data_card/TrailDataCard.vue";
import Loader from "../../utils/shared_components/Loader.vue";

// --- Helper/Model Imports (previously used in main.ts or here) ---
import createGeoLocate from '../../utils/helpers/GeoLocateHelper';
import getQueryParams from '../../utils/helpers/GetQueryParamsHelper';
import {HiikerMapStyle, SavedCenter, ThreeDimensionalMapManager} from "../../utils/models/MapStyles"; // Ensure models are correctly exported/imported
import {RevenueCatWrapper} from '../../utils/RevenueCatWrapper';
import {OnBoardingGoal} from "../../utils/models/OnBoardingGoal"; // Ensure this enum/type is defined
import {GridRefUtils} from "../../utils/helpers/GridRefPreference"; // Used in map init action, ensure accessible if needed directly
import {MeasurementPrefUtility} from "../../utils/helpers/MeasurementPref"; // Used in map init action
import {IndexedDBCache} from "../../utils/crosshair_altitude_service/indexed_db_cache"; // Used in map init action
import {getElevation} from "../../utils/crosshair_altitude_service/pixel_methods"; // Used in map init action
import {isClickOnWaypointHelper} from "../../utils/helpers/IsClickOnWaypointHelper";
import emitter from "../../utils/bus"; // Used in map init action
import { Modal } from 'bootstrap'
import {LayerRegistry} from "../../utils/layer-registry";
import { authReady } from "../../utils/firebase/authReady";
import MapDataPOIDetailDialog from '../map_data_poi_dialog/MapDataPOIDetailDialog.vue';
import { addZyxBoundariers } from '../../utils/addZyxBoundariers.js';
import MapOptionsDialog from "../map_options_dialog/MapOptionsDialog.vue";
import ScaleBar from "../components/ScaleBar.vue";

import MapContextMenu from "./components/MapContextMenu.vue";
import { openMapContextMenu } from "../../utils/helpers/mapContextMenu";
import MapInfoBar from "./components/MapInfoBar.vue";
import MapCrosshair from "./components/MapCrosshair.vue";
import MapInfoBarSettingsDialog from "./components/MapInfoBarSettingsDialog.vue";
import {
  applyLocationIconBodyClasses,
  CROSSHAIR_COLORS,
  MapInfoBarSettings,
  resolveFocusPoint,
  setMapInteractionsLocked,
} from "../../utils/mapInfoBarSettings";
import { GRID_REF_CHANGED_EVENT } from "../../utils/helpers/GridRefPreference";
import { USER_POSITION_CHANGED_EVENT } from "../../utils/helpers/GeoLocateHelper";
import HikeMeasurementPanel from "../hike_map/components/HikeMeasurementPanel.vue";
import LandAccessLegend from "../components/LandAccessLegend.vue";
import { reregisterInteractionHandlers as reregisterLandAccessHandlers } from "../base_map_dialog/helpers/LandAccessOverlay";
import { snapPoints } from "../../utils/models/Graphooper";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../utils/models/MarkerFeature";
import measurementMarker from "../../images/measurement_marker.png";
import { hiikerElevation } from "../../utils/helpers/HiikerElevationApiHelper";
import { ensurePrintMapAccess, setPrintMapEntitlement } from "../../utils/print_maps/printMapAccess";
import OnThisMapButtons from "./components/on_this_map/OnThisMapButtons.vue";


export default defineComponent({
  name: 'Map', // Component name
  components: {
    // List all used components
    Loader,
    TrailDataCard,
    MapControls,
    GoToLocationDialog,
    BaseMapStylesDialog,
    LoadingDialog,
    WaypointDialog,
    WaypointAddingNotification, /* MapDataTrailsTab, */
    MapDataPOIDetailDialog,
    MapOptionsDialog,
    ScaleBar,

    MapContextMenu,
    MapInfoBar,
    MapCrosshair,
    MapInfoBarSettingsDialog,
    HikeMeasurementPanel,
    LandAccessLegend,
    OnThisMapButtons
  },
  data() {
    return {
      // Local component state
      buttons: [
        {id: 0, name: 'Maps', icon: 'layers', enabled: true, dropIcon: true}, // Assuming always enabled
        {id: 1, name: 'Options', icon: 'add', enabled: false, dropIcon: false}, // Initial state depends on map state
      ],
      lngLatToShow: null as { lng: string, lat: string } | null, // Moved from root instance data
      mapInstance: null as mapboxgl.Map | null, // Store map locally for custom scale bar
      currentLatitude: 0 as number, // Track current latitude for scale calculations
      currentBearing: 0 as number, // Drives the info bar's mini compass
      /** Last mouse position over the map, in case the crosshair is switched off mid-session. */
      lastMouseLngLat: null as { lng: number; lat: number } | null,
      elevationCache: null as IndexedDBCache | null
    };
  },
  computed: {
    // Use mapState helper for Vuex state
    ...mapState({
      miniCoordsBar: (state: any) => state.map.miniCoordsBar,
      infoBarSettings: (state: any): MapInfoBarSettings => state.map.infoBarSettings,
      interactionsLocked: (state: any): boolean => state.map.interactionsLocked,
      loading: (state: any) => state.map.loading,
      miniLoader: (state: any) => state.mapDataOverlays?.miniLoader,
      landAccessActive: (state: any) => state.mapDataOverlays?.landAccessActive,
      // Example: Make sure the state path is correct for your store structure
      // _3dEnabled: (state: any) => state.map.is3D // Assuming 3D state is in map module
    }),
    ...mapState('hikeMeasurement', ['isActive']),
    crosshairColorHex(): string {
      return CROSSHAIR_COLORS[this.infoBarSettings.crosshairColor]?.hex ?? CROSSHAIR_COLORS[0].hex;
    },
    // You might need an explicit computed property if _3dEnabled logic is complex
    _3dEnabled(): boolean {
      // Replace with actual logic to get 3D mode status, e.g., from Vuex store
      // return this.$store.state.map.is3DEnabled;
      return false; // Placeholder
    }
  },
  watch: {
    // Crosshair on: the bar describes the map centre. Off: back to the mouse.
    'infoBarSettings.crosshairEnabled'() {
      this.refreshFocus();
    },
    // Watcher for 3D mode changes to update button state
    _3dEnabled(newVal: boolean) {
      const button3d = this.buttons.find(b => b.id === 1);
      if (button3d) {
        button3d.enabled = newVal;
      }
    },

    // Watcher for measurement mode changes to handle close button
    isActive(newVal: boolean, oldVal: boolean) {
      // If measurement mode is being turned off and we're on the main map
      if (!newVal && oldVal && window.mainMapMeasurementMode) {
        this.stopMainMapMeasurementMode();
      }
    },
  },
  methods: {
    // ─── Map information bar ───────────────────────────────────────────────
    openInfoBarSettings() {
      const el = document.getElementById('map-info-bar-settings-dialog');
      if (el) Modal.getOrCreateInstance(el).show();
    },
    resetNorth() {
      (this.mapInstance || window.map)?.easeTo({ bearing: 0 });
    },
    setInteractionsLocked(locked: boolean) {
      this.$store.commit('map/setInteractionsLocked', locked);
      setMapInteractionsLocked(this.mapInstance || window.map, locked);
    },
    handleInfoBarSettingsChanged(settings: MapInfoBarSettings) {
      this.$store.commit('map/setInfoBarSettings', settings);
      applyLocationIconBodyClasses(document.body, settings);
    },
    handleUserPositionChanged(fix: { lng: number; lat: number; accuracy: number | null }) {
      this.$store.commit('map/setUserPosition', fix);
    },
    handleGridRefChanged() {
      // Re-format the coordinates for the current focus point in the new system.
      const focus = this.$store.state.map.focusLngLat;
      if (focus) this.updateFocusReadout(focus);
    },
    /**
     * Recompute the focus point from the current source (crosshair → map centre, else mouse) and
     * refresh the readout. Called on crosshair toggle and, while the crosshair is on, on map move.
     */
    refreshFocus() {
      const map = this.mapInstance || window.map;
      if (!map) return;
      const crosshair = this.infoBarSettings.crosshairEnabled;
      const center = map.getCenter();
      const focus = resolveFocusPoint(crosshair, { lng: center.lng, lat: center.lat }, this.lastMouseLngLat);
      this.$store.commit('map/setFocus', { lngLat: focus, source: crosshair ? 'crosshair' : 'mouse' });
      if (focus) this.updateFocusReadout(focus);
    },
    async updateFocusReadout(focus: { lng: number; lat: number }) {
      const map = this.mapInstance || window.map;
      if (!map) return;
      const lngLat = new mapboxgl.LngLat(focus.lng, focus.lat);
      // Read the preference per call so a coordinate-system change applies without a reload.
      this.$store.commit('map/setFocusCoordinates', GridRefUtils.preference().convert(lngLat).toString());
      try {
        if (!this.elevationCache) this.elevationCache = new IndexedDBCache();
        const elevation = await getElevation(lngLat, map.getZoom(), this.elevationCache);
        // Only publish if this point is still the focus (a slower tile fetch must not win).
        const current = this.$store.state.map.focusLngLat;
        if (current && current.lng === focus.lng && current.lat === focus.lat) {
          this.$store.commit('map/setFocusElevation', elevation ?? '');
        }
      } catch {
        this.$store.commit('map/setFocusElevation', '');
      }
    },

    handleContextMenu(e: mapboxgl.MapMouseEvent & { originalEvent?: MouseEvent }) {
      openMapContextMenu(
        this.mapInstance || window.map,
        e,
        this.$store.state.waypoints.waypointAddingModeEnabled,
      );
    },

    // --- Map Initialization Logic (Moved from old main.ts root instance) ---
    async initializeMap() {
      mapboxgl.accessToken = "pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w"; // Consider using environment variables

      // Use this.$store provided by app.use(store)
      const initialSettings = await this.$store.getters['map/initialMapSettings'];

      // Access the map container element using the ref
      const mapContainer = this.$refs.mapElement as HTMLElement;
      if (!mapContainer) {
        console.error("Map container element #map not found during initialization.");
        return; // Stop initialization if container doesn't exist
      }
      initialSettings.container = mapContainer; // IMPORTANT: Set container via ref

      try {
        const map = new mapboxgl.Map(initialSettings);
        window.map = map; // Assign to global scope (if required by helpers/legacy code)
        this.mapInstance = map; // Store instance locally for custom scale bar
        this.currentLatitude = map.getCenter().lat; // Initialize latitude



        window.layerRegistry = new LayerRegistry(window.map);
        // Initialize related store modules AFTER map potentially created
        await this.$store.dispatch('waypoints/initialiseStore');

        // Ensure overlay listeners are registered early (before map-loaded fires)
        // so persisted overlays like land access restore reliably on refresh.
        this.$store.dispatch('mapDataOverlays/initOverlayListeners');

        // --- Map Event Listeners ---
        map.on('load', () => {
          console.log('Mapbox map loaded.');
          emitter.emit('map-loaded')
          createGeoLocate(); // Assumes this uses window.map
          console.log(`Mapbox map 'load': isStyleLoaded() = ${map.isStyleLoaded()}`); // <<< ADD THI
        });

        map.on('style.load', () => {
          console.log('Mapbox map style loaded.');
          emitter.emit('map-style-loaded');
          console.log(`Mapbox map 'style.load': isStyleLoaded() = ${map.isStyleLoaded()}`); // <<< ADD THI
          // Re-add measurement line if measurement mode is active
          this.$store.dispatch('hikeMeasurement/readdMeasurementToMap');
        });

        map.on('moveend', () => {
          // Assumes SavedCenter uses window.map or is passed the instance
          SavedCenter.saveCurrentCenter();
          // Optional: Update store with new center/zoom if needed elsewhere
          // this.$store.commit('map/setCenter', map.getCenter());
          // this.$store.commit('map/setZoom', map.getZoom());
        });

        // --- Map information bar readout (the single mousemove/move handler for coords+elevation) ---
        // Mouse mode: follow the cursor. Crosshair mode: follow the map centre on move.
        this.elevationCache = new IndexedDBCache();
        const handleMouseMove = (e: mapboxgl.MapMouseEvent & Object) => {
          this.lastMouseLngLat = { lng: e.lngLat.lng, lat: e.lngLat.lat };
          if (this.infoBarSettings.crosshairEnabled) return;
          this.$store.commit('map/setFocus', { lngLat: this.lastMouseLngLat, source: 'mouse' });
          void this.updateFocusReadout(this.lastMouseLngLat);
        };
        map.on('mousemove', throttle(handleMouseMove, 100));
        map.on('move', throttle(() => {
          if (this.infoBarSettings.crosshairEnabled) this.refreshFocus();
        }, 100));
        map.on('rotate', () => { this.currentBearing = map.getBearing(); });
        // Seed the bar with the initial centre so it isn't empty before the first mouse move.
        map.once('load', () => this.refreshFocus());

        // --- Click Handling (Copied from original MapModule action) ---
        map.on('click', (e: mapboxgl.MapMouseEvent & Object) => {
          console.log('Map clicked', e.lngLat);
          // Access rootState via this.$store.rootState or specific module state
          if (this.$store.state.waypoints.waypointAddingModeEnabled) {
            return; // Exit if adding waypoint
          }
          // Check if click was on a waypoint feature
          if (isClickOnWaypointHelper(map.queryRenderedFeatures(e.point))) {
            return; // Exit if waypoint clicked
          }
          // Add other map click logic here if needed
        });
        map.on('contextmenu', this.handleContextMenu);

        // --- Handle Initial Position from Query Params ---
        const queryParams = getQueryParams(window.location.href);
        if (queryParams.lng && queryParams.lat) {
          const lng = parseFloat(queryParams.lng);
          const lat = parseFloat(queryParams.lat);
          if (!isNaN(lng) && !isNaN(lat)) {
            this.lngLatToShow = {lng: queryParams.lng, lat: queryParams.lat};
            // Wait until style is loaded before potentially moving the map
            map.once('styledata', () => { // Use 'styledata' or 'load' event
              map.setCenter([lng, lat]);
              if (queryParams.zoom) {
                const zoom = parseFloat(queryParams.zoom);
                if (!isNaN(zoom)) map.setZoom(zoom);
              }
            });
          } else {
            console.warn("Invalid lng/lat query parameters:", queryParams.lng, queryParams.lat);
          }
        }

        // --- Update latitude on map move for accurate scale calculations ---
        map.on('move', () => {
          this.currentLatitude = map.getCenter().lat;
        });

        // --- Final step: Dispatch action indicating map is fully initialized ---
        await this.$store.dispatch('map/mapInitialised'); // Signal Vuex store
        console.log('Map initialization sequence complete.');

      } catch (error) {
        console.error("Error initializing Mapbox map:", error);
        // Handle initialization error (e.g., show error message)
        this.$store.commit('map/setLoading', false); // Ensure loading indicator is turned off
      }
    },

    // --- Button Click Handler ---
    async buttonClicked(id: number) {
      await authReady; // Wait for Firebase auth sync to complete (sets window.user)
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus(); // Check subscription status
      setPrintMapEntitlement(isProOrProPlus); // Opening Options is the tap before Print — reuse this answer there
      const userExists = !!window.user; // Check if user object exists

      switch (id) {
        case 0: // Change Map Style
          if (userExists) {
            const mapStylesDialog = document.getElementById("map-styles-dialog");
            const modal = new Modal(mapStylesDialog);
            modal.show();
          } else {
            window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles);
          }
          break;
        case 1: // Options Modal
          const optionsEl = document.getElementById('map-options-dialog');
          if (optionsEl) Modal.getOrCreateInstance(optionsEl).show();
          break;

        case 2: // Search / Go To Location
          const element = document.getElementById("go-to-location-dialog")
          const modal = new Modal(element)
          modal.show()
          break;

        case 3: // Plan a Route
          if (window.map) { // Ensure map is available
            const center = window.map.getCenter();
            const zoom = window.map.getZoom();
            const params = `?lat=${center.lat.toFixed(6)}&lng=${center.lng.toFixed(6)}&zoom=${zoom.toFixed(2)}`;
            window.location.href = '/hike-planner' + params; // Consider Vue Router: this.$router.push(...)
          } else {
            console.error("Map not available for planning route.");
          }
          break;

        case 5: // Add to Map (Waypoints/Data)
          if (userExists) {
            const element = document.getElementById("add-to-map-dialog")
            const modal = new Modal(element)
            modal.show()
          } else {
            window.onBoardingVue?.launchFor(OnBoardingGoal.Waypoints);
          }
          break;

        case 6: // Print Map
          window.location.href = '/print-maps'; // Consider Vue Router: this.$router.push(...)
          break;

        case 7: // Waypoint Options
          if (userExists) {
            const element = document.getElementById("waypoint-options-dialog")
            const modal = new Modal(element)
            modal.show()
          } else {
            window.onBoardingVue?.launchFor(OnBoardingGoal.Waypoints);
          }
          break;

        // Removed duplicate case 7 from original code. If it was intended
        // for 'Vector Data' or similar, assign a new ID and handle it.
        /*
        case SOME_OTHER_ID: // Vector Dialog Example
          if (userExists) {
            console.warn("jQuery modal call needs replacement: #vector-dialog");
            $('#vector-dialog').modal('show'); // VUE REPLACEMENT: Use component state (v-if)
          } else {
            window.onBoardingVue?.launchFor(OnBoardingGoal.CreateAccount);
          }
          break;
        */
      }
    },
    reverseRoute() {
      try { emitter.emit('map:reverse-route'); } catch (e) { console.log('reverse-route not wired'); }
      const mo = document.getElementById('map-options-dialog'); if (mo) Modal.getOrCreateInstance(mo).hide();
    },
    openWaypoints() {
      const mo = document.getElementById('map-options-dialog');
      if (mo) Modal.getOrCreateInstance(mo).hide();

      if (window.user?.firebase_user_id) {
        const el = document.getElementById('add-to-map-dialog') || document.getElementById('waypoint-options-dialog');
        if (el) Modal.getOrCreateInstance(el).show();
      } else {
        window.onBoardingVue?.launchFor(OnBoardingGoal.Waypoints);
      }
    },
    showGoTo() {
      const el = document.getElementById('go-to-location-dialog');
      if (el) { Modal.getOrCreateInstance(el).show(); }
      const mo = document.getElementById('map-options-dialog'); if (mo) Modal.getOrCreateInstance(mo).hide();
    },
    async openMeasurement() {
      const isPro = await RevenueCatWrapper.isProOrProPlus();
      if (!isPro) {
        window.location.href = '/purchases';
        return;
      }

      // For the main map, create a completely separate measurement system
      // that doesn't rely on the existing Vuex module
      await this.startMainMapMeasurementMode();

      const mo = document.getElementById('map-options-dialog');
      if (mo) Modal.getOrCreateInstance(mo).hide();
    },

    // Main map measurement system
    async startMainMapMeasurementMode() {
      // Initialize main map measurement state
      window.mainMapMeasurementMarkers = [];
      window.mainMapMeasurementMode = true;

      // Remove any existing click handlers first - remove ALL click handlers
      window.map.off('click');

      // Set up map click handler for main map measurement
      window.map.on('click', this.handleMainMapMeasurementClick);

      // Initialize measureableGeoJson with empty FeatureCollection to prevent null reference errors
      const emptyMeasureableGeoJson = {
        type: 'FeatureCollection',
        features: []
      };
      this.$store.commit('hikeMeasurement/setMeasureableGeoJson', emptyMeasureableGeoJson);

      // Set measurement state directly WITHOUT using Vuex actions that register conflicting handlers
      this.$store.commit('hikeMeasurement/setMeasurementMode', true);
      this.$store.commit('hikeMeasurement/setIsActive', true);

      // Expose the stop method globally for the measurement panel
      this.exposeStopMethod();

      console.log('Main map measurement mode started');
    },

    // Handle main map measurement clicks
    async handleMainMapMeasurementClick(event: mapboxgl.MapMouseEvent) {
      if (!window.mainMapMeasurementMode) return;

      const markers = window.mainMapMeasurementMarkers || [];

      if (markers.length >= 2) {
        return;
      }

      // Create marker elements directly (don't rely on Vuex getters)
      const markerElement = this.createMeasurementMarkerElement();

      const markerOption: mapboxgl.MarkerOptions = {
        element: markerElement,
        draggable: true,
        anchor: 'center',
        offset: [0, -12]
      };

      const marker = new mapboxgl.Marker(markerOption).setLngLat(
        event.lngLat.toArray()
      ).addTo(window.map);

      // Set up drag handler
      marker.on('dragend', async (e) => {
        await this.handleMainMapMarkerDragged(e);
      });

      // Store the marker in the global array
      markers.push(marker);
      window.mainMapMeasurementMarkers = markers;

      // If we have two markers, create a GraphHopper route between them
      if (markers.length === 2) {
        await this.createGraphHopperRouteBetweenMarkers(markers);
      }

      // Update measurement line
      await this.updateMainMapMeasurementLine();
    },

    // Handle main map marker drag
    async handleMainMapMarkerDragged(event: any) {
      const markers = window.mainMapMeasurementMarkers || [];

      // Update the marker position
      const markerLocation = event.target.getLngLat();
      event.target.setLngLat([markerLocation.lng, markerLocation.lat]);

      // If we have two markers, recreate the GraphHopper route
      if (markers.length === 2) {
        await this.createGraphHopperRouteBetweenMarkers(markers);
      }

      // Update measurement line
      await this.updateMainMapMeasurementLine();
    },

    // Create measurement marker element
    createMeasurementMarkerElement(): HTMLElement {
      const el = document.createElement('div');
      el.id = 'main-map-measurement-marker-' + Math.random();
      el.style.position = 'absolute';
      el.style.width = '26px';
      el.style.height = '83px';
      el.style.backgroundSize = 'contain';
      el.style.backgroundRepeat = 'no-repeat';
      el.style.cursor = 'pointer';
      el.style.backgroundPosition = 'center center';
      el.style.backgroundImage = `url(${measurementMarker})`;
      return el;
    },

    // Update main map measurement line
    async updateMainMapMeasurementLine() {
      const markers = window.mainMapMeasurementMarkers || [];

      if (markers.length < 2) {
        // Remove existing measurement line
        this.removeMainMapMeasurementLine();
        return;
      }

      const marker1 = markers[0];
      const marker2 = markers[1];

      const location1 = marker1.getLngLat().toArray();
      const location2 = marker2.getLngLat().toArray();

      // For main map, we use the GraphHopper route that was already created
      // Check if we have measureableGeoJson from GraphHopper
      const measureableGeoJson = this.$store.state.hikeMeasurement.measureableGeoJson;

      let measurementLine;

      if (measureableGeoJson && measureableGeoJson.features && measureableGeoJson.features.length > 0) {
        // Use the GraphHopper route for measurement
        const graphHopperFeature = measureableGeoJson.features[0];
        measurementLine = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: graphHopperFeature.geometry.coordinates[0] // Extract coordinates from MultiLineString
          }
        };
      } else {
        // Fallback: create a direct line between points
        measurementLine = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [location1, location2]
          }
        };
      }

      // Update the measurement line on the map
      this.updateMainMapMeasurementLineLayer(measurementLine);

      // Update Vuex store with measurement data for the panel using mutation instead of action
      this.$store.commit('hikeMeasurement/setMeasuredGeoJson', measurementLine);
    },

    // Remove main map measurement line
    removeMainMapMeasurementLine() {
      const map = window.map;
      if (!map) return;

      if (map.getLayer('main-map-measurement')) {
        map.removeLayer('main-map-measurement');
      }
      if (map.getSource('main-map-measurement')) {
        map.removeSource('main-map-measurement');
      }
    },

    // Update main map measurement line layer
    updateMainMapMeasurementLineLayer(measurementLine: any) {
      const map = window.map;
      if (!map) return;

      // Remove existing measurement layer
      this.removeMainMapMeasurementLine();

      // Add new measurement layer
      map.addSource('main-map-measurement', {
        type: 'geojson',
        data: measurementLine
      });

      map.addLayer({
        id: 'main-map-measurement',
        type: 'line',
        source: 'main-map-measurement',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#4CAF50',
          'line-width': 5,
        }
      });
    },

    // Helper method to create GraphHopper route between two markers
    async createGraphHopperRouteBetweenMarkers(markers) {
      if (markers.length !== 2) return;

      const marker1 = markers[0];
      const marker2 = markers[1];

      const marker1Location = marker1.getLngLat();
      const marker2Location = marker2.getLngLat();

      // Create temporary marker features for GraphHopper
      const startMarker = new MarkerFeature(
        new MarkerGeometry([marker1Location.lng, marker1Location.lat]),
        new MarkerProperties('temp-start', false, Date.now().toString(), 0)
      );

      const endMarker = new MarkerFeature(
        new MarkerGeometry([marker2Location.lng, marker2Location.lat]),
        new MarkerProperties('temp-end', false, Date.now().toString(), 0)
      );

      try {
        // Check if we should use straight line mode
        const routingMode = window.mainMapRoutingMode || 'hike';

        if (routingMode === 'straight') {
          // Use hiikerElevation to get elevation data for straight line mode
          const lineString = await hiikerElevation(startMarker, endMarker);

          // Convert the LineString to MultiLineString format expected by measurement tool
          const multiLineStringFeature = {
            type: 'Feature',
            geometry: {
              type: 'MultiLineString',
              coordinates: [lineString.lineStringGeometry.coordinates]
            },
            properties: {
              routeStyle: 'route'
            }
          };

          // Update the measureable GeoJSON with the elevation-enhanced straight line
          const updatedMeasureableGeoJson = {
            type: 'FeatureCollection',
            features: [multiLineStringFeature]
          };

          this.$store.commit('hikeMeasurement/setMeasureableGeoJson', updatedMeasureableGeoJson);
          console.log('Elevation-enhanced straight line route created for measurement');
        } else {
          // Use GraphHopper to create a route between the markers
          const graphHopperProfile = routingMode;
          const lineString = await snapPoints(startMarker, endMarker, graphHopperProfile);

          // Convert the LineString to MultiLineString format expected by measurement tool
          const multiLineStringFeature = {
            type: 'Feature',
            geometry: {
              type: 'MultiLineString',
              coordinates: [lineString.lineStringGeometry.coordinates]
            },
            properties: {
              routeStyle: 'route'
            }
          };

          // Update the measureable GeoJSON with the GraphHopper route using mutation
          const updatedMeasureableGeoJson = {
            type: 'FeatureCollection',
            features: [multiLineStringFeature]
          };

          this.$store.commit('hikeMeasurement/setMeasureableGeoJson', updatedMeasureableGeoJson);
          console.log(`GraphHopper route created for measurement with mode: ${routingMode}`);
        }
      } catch (error) {
        console.error('Error creating route:', error);

        // Fallback: create a direct line between points
        const directLineFeature = {
          type: 'Feature',
          geometry: {
            type: 'MultiLineString',
            coordinates: [[
              [marker1Location.lng, marker1Location.lat],
              [marker2Location.lng, marker2Location.lat]
            ]]
          },
          properties: {
            routeStyle: 'route'
          }
        };

        const fallbackMeasureableGeoJson = {
          type: 'FeatureCollection',
          features: [directLineFeature]
        };

        this.$store.commit('hikeMeasurement/setMeasureableGeoJson', fallbackMeasureableGeoJson);
      }
    },

    // Stop main map measurement mode
    stopMainMapMeasurementMode() {
      // Clean up global state
      window.mainMapMeasurementMode = false;

      // Remove click handler
      window.map.off('click', this.handleMainMapMeasurementClick);

      // Remove markers
      const markers = window.mainMapMeasurementMarkers || [];
      markers.forEach((marker) => {
        marker.remove();
      });
      window.mainMapMeasurementMarkers = [];

      // Remove measurement line
      this.removeMainMapMeasurementLine();

      // Clean up Vuex store state
      this.$store.commit('hikeMeasurement/setMeasurementMode', false);
      this.$store.commit('hikeMeasurement/setIsActive', false);
      this.$store.commit('hikeMeasurement/setMeasuredGeoJson', null);
      this.$store.commit('hikeMeasurement/setMeasureableGeoJson', null);

      // Re-register land access click handlers (wiped by map.off('click'))
      reregisterLandAccessHandlers();

      console.log('Main map measurement mode stopped');
    },

    // Expose the stop method globally for the measurement panel
    exposeStopMethod() {
      window.stopMainMapMeasurementMode = this.stopMainMapMeasurementMode.bind(this);
      window.updateMainMapMeasurementLine = this.updateMainMapMeasurementLine.bind(this);
      window.removeMainMapMeasurementLine = this.removeMainMapMeasurementLine.bind(this);
      window.mainMapRoutingMode = 'hike'; // Default routing mode
    },
    async openPrintMap() {
      if (!await ensurePrintMapAccess()) return;

      try {
        const map = this.mapInstance || window.map;
        if (map) {
          const center = map.getCenter();
          const zoom = map.getZoom();
          const params = new URLSearchParams({
            lat: center.lat.toFixed(6),
            lng: center.lng.toFixed(6),
            zoom: zoom.toFixed(2)
          });
          window.location.href = `/print-maps?${params.toString()}`;
          return;
        }
      } catch (e) {}
      window.location.href = '/print-maps';
    },
    openPlanner() {
      try {
        if (window.map) {
          const center = window.map.getCenter();
          const zoom = window.map.getZoom();
          const params = `?lat=${center.lat.toFixed(6)}&lng=${center.lng.toFixed(6)}&zoom=${zoom.toFixed(2)}`;
          window.location.href = '/hike-planner' + params;
        } else {
          window.location.href = '/hike-planner';
        }
      } catch (e) {
        window.location.href = '/hike-planner';
      }
    }
  },
  // --- Vue Lifecycle Hooks ---
  async beforeCreate() {
    // Logic from old root instance's beforeCreate (e.g., initializing non-map stores)
    // Use this.$store provided via app.use()
    try {
      await this.$store.dispatch('mapStyles/initialiseStore');
    } catch (error) {
      console.error("Error initializing mapStyles store:", error);
    }
  },
  async mounted() {
    // Entry point for map initialization after component DOM is ready
    console.log("Map component mounted, initializing map...");
    await this.initializeMap(); // Call the consolidated map setup method
    applyLocationIconBodyClasses(document.body, this.infoBarSettings);
    emitter.on('map-info-bar-settings:changed', this.handleInfoBarSettingsChanged);
    emitter.on(USER_POSITION_CHANGED_EVENT, this.handleUserPositionChanged);
    emitter.on(GRID_REF_CHANGED_EVENT, this.handleGridRefChanged);
    emitter.on('add-weather-legend', this.addWeatherLegend);
    emitter.on('remove-weather-legend', this.removeWeatherLegend);
  },
  beforeUnmount() {
    // Clean up map instance and listeners to prevent memory leaks
    console.log("Map component unmounting, removing map instance...");
    if (this.mapInstance) {
      this.mapInstance.off('contextmenu', this.handleContextMenu);
    } else if (window.map) {
      window.map.off('contextmenu', this.handleContextMenu);
    }
    if (window.map) {
      window.map.remove(); // Properly clean up Mapbox GL JS instance
      window.map = undefined;    // Remove global reference
    }
    this.mapInstance = null; // Clear local map reference

    emitter.off('map-info-bar-settings:changed', this.handleInfoBarSettingsChanged);
    emitter.off(USER_POSITION_CHANGED_EVENT, this.handleUserPositionChanged);
    emitter.off(GRID_REF_CHANGED_EVENT, this.handleGridRefChanged);
    emitter.off('add-weather-legend', this.addWeatherLegend);
    emitter.off('remove-weather-legend', this.removeWeatherLegend);
    // if (this.mapInstance) {
    //   this.mapInstance.remove();
    //   this.mapInstance = null;
    // }
    // Remove any other listeners manually added if necessary
  }
});
</script>

<style>
/* Styles remain the same as in your original post */

.map-container {
  flex: 1 1 auto;
  max-width: 100%;
  position: relative; /* Changed from //position: relative */
  z-index: 7;
  height: 90vh; /* Fallback height for browsers without svh support */
}

.map-content {
  width: 100%;
  height: 100%;
  position: relative;
}

/* Full-screen mode (toggled from MapControls via utils/helpers/mapFullscreen.ts).
   The body class fixes the map to the viewport and hides the navbar. z-index stays
   below Bootstrap's modal layer (1050) so the Maps/Options/GoTo dialogs teleported to
   <body> still render on top of the map. */
body.map-fullscreen {
  overflow: hidden;
}
body.map-fullscreen .map-container {
  position: fixed;
  inset: 0;
  height: 100vh;
  height: 100dvh;
  max-width: none;
  z-index: 1040;
}

.map-lock-badge {
  position: absolute;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgb(27 27 29 / 90%);
  color: #fddf00;
  font-size: .75rem;
  pointer-events: none;
}
.map-lock-badge .material-icons { font-size: 14px; }

/* Ensure map takes up space */
.map-custom-class {
  position: absolute; /* Added for positioning within map-content */
  top: 0;
  bottom: 0;
  left: 0; /* Added */
  right: 0; /* Added */
  width: 100%;
  height: 100%; /* Ensure full height within parent */
  z-index: 2;
}


#weather-legend-container{
  position: absolute;
  bottom: 55px;
  left: 20px;
  z-index: 20000000;
  /* opacity: 1; */
  max-width: 76%;
  width: 310px;
}
#weather-legend-container img{
  max-width: 100%;
}


.modal, .modal-backdrop{
  background-color: rgb(0 0 0 / 30%) !important;
}

/* Keep existing styles... */
.a-little-class {
  display: none;
}
</style>
